// Builds the Network Map's tree from assets (/api/servers) and the topology
// report (/api/network/topology): Internet → router → areas → access points
// (and switches) → devices. A device's place comes from its agent's Wi-Fi
// report, else from an SNMP bridge table (placements). Areas come from an access
// point's area (set by people), else its SNMP location, else the location most
// of its devices carry; wired devices and devices that have not reported a link
// yet fall into the area of their own location.
import { ago, hasAgent, isSilent, statusOf, worstFinding } from './model';

export const WEAK_DBM = -75;
export const SHOWN_PER_GROUP = 4;
export const NO_AGENT_AREA = '__no_agent__';
const DEFAULT_AREA = 'Unassigned';
const PHONE_NAME = /(^|[^a-z])(iphone|ipad|android|galaxy|honor|huawei|redmi|xiaomi|poco|oppo|vivo|realme|oneplus|pixel|nova)([^a-z]|$)/;
const TV_NAME = /(^|[^a-z])(tv|smarttv|androidtv|bravia|roku|chromecast|firetv|fire-tv|appletv|apple-tv|sonos|ipcam|camera)([^a-z]|$)/;
const PRINTER_NAME = /(^|[^a-z])(printer|epson|canon|brother|laserjet|deskjet|officejet)([^a-z]|$)/;
const SERVER_PORTS = [22, 25, 389, 445, 1433, 3306, 3389, 5432, 6379, 9200, 27017];

const majority = (values) => {
  const n = new Map();
  values.filter(Boolean).forEach(v => n.set(v, (n.get(v) || 0) + 1));
  return [...n.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] || null;
};

// The routers the Worker reports: one main router per location, plus any second gateway two or more
// agents use. An older Worker only sends `gateway`, which then stands for every location (location null).
export const gatewaysOf = (topo) => {
  if (topo?.gateways?.length) return topo.gateways;
  return topo?.gateway ? [{ ...topo.gateway, location: null, primary: true }] : [];
};

// The main router for a location, or null when none is known there.
export const gatewayFor = (topo, location) =>
  gatewaysOf(topo).find(g => g.primary && (g.location === null || g.location === (location || ''))) || null;

export const apLabel = (ap) => ap.name || `${ap.vendor || 'Access point'} ${ap.key.slice(-5).toUpperCase()}`;

export const signalBars = (dbm) => {
  if (dbm == null) return 0;
  if (dbm >= -55) return 4;
  if (dbm >= -65) return 3;
  if (dbm >= WEAK_DBM) return 2;
  return 1;
};

export const uptime = (s) => (s >= 86400 ? `${Math.floor(s / 86400)} d` : s >= 3600 ? `${Math.floor(s / 3600)} h` : `${Math.max(1, Math.floor(s / 60))} min`);

// One line of SNMP health for an access point or switch.
export const snmpLine = (snmp) => {
  if (!snmp) return null;
  if (snmp.stale) return { text: `SNMP last answered ${ago(snmp.polled_at)} ago`, warn: true };
  const parts = [
    snmp.uptime_s != null && `up ${uptime(snmp.uptime_s)}`,
    snmp.ports_total > 0 && `${snmp.ports_up}/${snmp.ports_total} ports up`,
    snmp.port_errors > 0 && `${snmp.port_errors} port errors`,
  ].filter(Boolean);
  return { text: parts.join(' · ') || 'Answers SNMP', warn: snmp.port_errors > 0 };
};

export const isWeak = (link) => link?.type === 'wifi' && link.signal_dbm != null && link.signal_dbm < WEAK_DBM;

// What makes a device worth showing before the rest: risk first, then open
// findings, then something wrong with its link or heartbeat.
export const importance = (a, link) => {
  const sev = { crit: 4, high: 3, med: 2, low: 1 }[worstFinding(a.findings)] || 0;
  return (a.risk ?? -1) * 10 + sev * 3 + (isWeak(link) ? 5 : 0) + (isSilent(a) ? 4 : 0) + (link?.gateway_mismatch ? 10000 : 0);
};

// What an agentless device probably is, for grouping the "No agent" area.
export const categoryOf = (a) => {
  const ports = new Set(a.ports.map(p => p.port));
  const vendor = (a.vendor || '').toLowerCase();
  const type = (a.deviceType || '').toLowerCase();
  if (type === 'printer' || [9100, 631, 515].some(p => ports.has(p))) return 'Printers';
  if (a.kind === 'network') return 'Network devices';
  if (type === 'iot' || type === 'iot_device' || /samsung|lg elec|roku|sonos|hikvision|dahua|espressif|tuya|xiaomi|amazon|google/.test(vendor)) return 'TVs, cameras & IoT';
  if (/apple|huawei|honor|oppo|vivo|oneplus|motorola/.test(vendor)) return 'Phones & tablets';
  // Discovery files any device with an unrecognised vendor as a server, so only call it one
  // when it answers on a server port.
  if (a.kind === 'server' && SERVER_PORTS.some(p => ports.has(p))) return 'Servers';
  if (/altobeam|prime electronics/.test(vendor)) return 'TVs, cameras & IoT';
  // Phones hide their maker behind a random MAC, but their name often says what they are.
  const name = (a.hostname || '').toLowerCase();
  if (PHONE_NAME.test(name)) return 'Phones & tablets';
  if (TV_NAME.test(name)) return 'TVs, cameras & IoT';
  if (PRINTER_NAME.test(name)) return 'Printers';
  if (a.kind === 'server' && !a.discovered) return 'Servers';
  if (a.kind === 'endpoint') return 'Computers';
  return 'Unknown';
};

const summarize = (members, linkOf) => {
  const s = { total: members.length, findings: 0, weak: 0, late: 0, mismatch: 0, crit: 0, high: 0, med: 0, low: 0, unk: 0 };
  members.forEach(a => {
    s.findings += a.findings?.total || 0;
    if (isWeak(linkOf(a.id))) s.weak += 1;
    if (isSilent(a)) s.late += 1;
    if (linkOf(a.id)?.gateway_mismatch) s.mismatch += 1;
    s[a.band] += 1;
  });
  s.attention = s.crit + s.high > 0 || s.mismatch > 0;
  return s;
};

/**
 * @returns {{
 *   router, networkDevices, areas: Array<{id, name, aps, wired, unlinked, groups, summary, members}>,
 *   accessPoints, suspicious, mismatched, knownCount, newCount, snmpDevices,
 *   linkOf, placementOf, groupKeyOf, areaOf
 * }}
 * `aps` in an area also holds switches (kind 'switch') that SNMP placed devices behind.
 */
export function buildTopology(assets, topo) {
  const links = new Map((topo?.links || []).map(l => [l.server_id, l]));
  const linkOf = (id) => links.get(id) || null;
  const byId = new Map(assets.map(a => [a.id, a]));
  const gateway = topo?.gateway || null;
  const routerAsset = gateway?.asset_id ? byId.get(gateway.asset_id) || null : null;
  const gateways = gatewaysOf(topo).map(g => ({ ...g, asset: g.asset_id ? byId.get(g.asset_id) || null : null }));
  const routerIds = new Set(gateways.map(g => g.asset_id).filter(Boolean));

  // SNMP: polled devices (health, the access point each is) and where bridge tables put assets.
  const snmpDevices = topo?.snmp_devices || [];
  const snmpOfAp = new Map(snmpDevices.filter(d => d.ap_key).map(d => [d.ap_key, d]));
  const snmpByKey = new Map(snmpDevices.map(d => [deviceKey(d.asset_id, d.ip, d.location), d]));
  const placements = new Map((topo?.placements || []).map(p => [p.server_id, p]));
  const placementOf = (id) => placements.get(id) || null;

  // Access points (and, from SNMP, switches) and their clients.
  const aps = new Map((topo?.access_points || []).map(ap => {
    const snmp = snmpOfAp.get(ap.key) || null;
    return [ap.key, { ...ap, kind: 'ap', label: apLabel(ap), snmp, asset: snmp?.asset_id ? byId.get(snmp.asset_id) || null : null, clients: [] }];
  }));
  const groupFor = (p) => {
    if (p.ap_key) {
      if (!aps.has(p.ap_key)) aps.set(p.ap_key, { key: p.ap_key, kind: 'ap', name: null, area: null, status: 'new', ssids: [], label: apLabel({ key: p.ap_key }), snmp: snmpOfAp.get(p.ap_key) || null, asset: null, clients: [] });
      return aps.get(p.ap_key);
    }
    // A polled device no Wi-Fi agent has reported: it heads its own group, named from SNMP.
    const key = deviceKey(p.device_asset_id, p.device_ip, p.device_location);
    if (!aps.has(key)) {
      const snmp = snmpByKey.get(key) || null;
      const asset = p.device_asset_id ? byId.get(p.device_asset_id) || null : null;
      const name = snmp?.sys_name || (asset && !asset.unnamed ? asset.host : null) || p.device_ip;
      aps.set(key, { key, kind: snmp?.wireless ? 'ap' : 'switch', polled: true, name, label: name, area: null, status: 'known', ssids: [], snmp, asset, clients: [] });
    }
    return aps.get(key);
  };
  // Devices that head a group are drawn as that group, not again as a device or in the backbone.
  const heads = new Set([
    ...snmpDevices.filter(d => d.asset_id && d.ap_key && aps.has(d.ap_key)).map(d => d.asset_id),
    ...[...placements.values()].map(p => p.device_asset_id).filter(Boolean),
  ]);
  routerIds.forEach(id => heads.delete(id));

  const networkDevices = assets.filter(a => a.kind === 'network' && !routerIds.has(a.id) && !heads.has(a.id));
  const netIds = new Set(networkDevices.map(a => a.id));
  const placeable = assets.filter(a => !routerIds.has(a.id) && !netIds.has(a.id) && !heads.has(a.id));

  const memberOf = new Map();
  const wired = [];
  const unlinked = [];
  const noAgent = [];
  placeable.forEach(a => {
    const link = linkOf(a.id);
    const place = placementOf(a.id);
    if (link?.type === 'wifi' && link.ap_key) {
      if (!aps.has(link.ap_key)) aps.set(link.ap_key, { key: link.ap_key, kind: 'ap', name: null, area: null, status: 'new', ssids: link.ssid ? [link.ssid] : [], label: apLabel({ key: link.ap_key }), snmp: null, asset: null, clients: [] });
      aps.get(link.ap_key).clients.push(a);
      memberOf.set(a.id, link.ap_key);
    } else if (place) {
      const group = groupFor(place);
      group.clients.push(a);
      memberOf.set(a.id, group.key);
    } else if (!hasAgent(a) && !link) noAgent.push(a);
    else if (link?.type === 'wired') wired.push(a);
    else unlinked.push(a);
  });

  const sortGroup = (list) => list.sort((x, y) => importance(y, linkOf(y.id)) - importance(x, linkOf(x.id)) || x.host.localeCompare(y.host));
  const areaMap = new Map();
  const areaFor = (name) => {
    const key = name || DEFAULT_AREA;
    if (!areaMap.has(key)) areaMap.set(key, { id: key, name: key, aps: [], wired: [], unlinked: [], groups: [] });
    return areaMap.get(key);
  };

  [...aps.values()].forEach(ap => {
    sortGroup(ap.clients);
    ap.bands = [...new Set(ap.clients.map(c => linkOf(c.id)?.band).filter(Boolean))].sort();
    ap.summary = summarize(ap.clients, linkOf);
    ap.areaName = ap.area || ap.snmp?.sys_location || majority(ap.clients.map(c => c.location)) || DEFAULT_AREA;
    areaFor(ap.areaName).aps.push(ap);
  });
  wired.forEach(a => areaFor(a.location || 'Wired').wired.push(a));
  unlinked.forEach(a => areaFor(a.location).unlinked.push(a));

  const areas = [...areaMap.values()].map(area => {
    area.aps.sort((x, y) => (y.suspicious - x.suspicious) || (y.clients.length - x.clients.length));
    sortGroup(area.wired);
    sortGroup(area.unlinked);
    const members = [...area.aps.flatMap(ap => ap.clients), ...area.wired, ...area.unlinked];
    // The routers of the locations its devices are in (all of them, for an older Worker).
    const locations = new Set(members.map(a => a.location || ''));
    const routers = gateways.filter(g => g.location === null || locations.has(g.location));
    return { ...area, members, summary: summarize(members, linkOf), suspicious: area.aps.some(ap => ap.suspicious), gateways: routers };
  }).sort((x, y) => (y.suspicious - x.suspicious) || (y.summary.attention - x.summary.attention) || (y.summary.total - x.summary.total));

  if (noAgent.length) {
    const groups = new Map();
    noAgent.forEach(a => { const c = categoryOf(a); groups.set(c, [...(groups.get(c) || []), a]); });
    areas.push({
      id: NO_AGENT_AREA, name: 'No agent', aps: [], wired: [], unlinked: [], gateways: [],
      groups: [...groups.entries()].map(([name, list]) => ({ name, members: list.sort((x, y) => x.host.localeCompare(y.host)) }))
        .sort((x, y) => y.members.length - x.members.length),
      members: noAgent,
      summary: { ...summarize(noAgent, linkOf), attention: false, newThisWeek: noAgent.filter(a => a.discovered && Date.now() - Date.parse(a.created_at || 0) < 7 * 86400_000).length },
    });
  }

  const areaOf = new Map();
  areas.forEach(area => area.members.forEach(a => areaOf.set(a.id, area.id)));
  const accessPoints = [...aps.values()].filter(ap => ap.kind === 'ap');

  return {
    gateway,
    gateways,
    router: routerAsset,
    networkDevices,
    areas,
    accessPoints,
    suspicious: accessPoints.filter(ap => ap.suspicious),
    mismatched: placeable.filter(a => linkOf(a.id)?.gateway_mismatch),
    knownCount: accessPoints.filter(ap => ap.status === 'known').length,
    newCount: accessPoints.filter(ap => ap.status !== 'known').length,
    snmpDevices,
    linkOf,
    placementOf,
    groupKeyOf: (id) => memberOf.get(id) || null,
    areaOf: (id) => areaOf.get(id) || null,
  };
}

// Two locations can both have a polled device at 192.168.0.5, so an unregistered one is keyed by place too.
const deviceKey = (assetId, ip, location) => `dev:${assetId || `${location || ''}|${ip}`}`;

// A device's AP (or group) key, so search can open the right list.
export const groupOf = (tree, id) => {
  const key = tree.groupKeyOf(id);
  if (key) return `ap:${key}`;
  const area = tree.areas.find(a => a.id === tree.areaOf(id));
  if (!area) return null;
  if (area.id === NO_AGENT_AREA) return `cat:${area.groups.find(g => g.members.some(m => m.id === id))?.name}`;
  if (area.wired.some(m => m.id === id)) return `wired:${area.id}`;
  return `unlinked:${area.id}`;
};

export const statusDot = (a) => (hasAgent(a) ? statusOf(a).dot : null);
